import { Button, Dialog, Input, Select, Textarea } from '@artosera/ui';
import {
  formatDate,
  MOTIFS_PUBLICS_ANNULATION,
  type MotifPublicAnnulation,
} from '@artosera/shared';
import { Download } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import {
  useCertificatsOeuvre,
  useEmettreCertificat,
  useOuvrirCertificatPdf,
  useAnnulerCertificat,
  type CorpsEmissionCertificat,
} from '../../../api/documentsGalerie.js';
import { useProvenance } from '../../../api/provenance.js';
import { useEditionsOeuvre, type OeuvrePresentee } from '../../../api/oeuvres.js';
import { Field } from '../../Field.js';
import { ouvrirUrlSignee } from '../../../api/client.js';
import styles from './Tabs.module.css';
import { Verrouillable } from '../../module/LectureSeule.js';

export interface CertificatsTabProps {
  galerieSlug: string;
  oeuvre: OeuvrePresentee;
  /** Droit d'écriture (`documents:write`) : sans lui, pas de bouton d'émission (ARTO-53, § 4). */
  peutEmettre: boolean;
  peutAnnuler: boolean;
  provenanceActive?: boolean;
}

/**
 * Certificats d'authenticité (3.3, écran 2, § 19.5) : liste des certificats
 * émis, bouton d'émission avec confirmation — l'émission est un ACTE
 * numéroté et irréversible, pas un brouillon qu'on republie.
 *
 * Un certificat émis lors d'une session PRÉCÉDENTE s'ouvre par
 * `GET …/certificats/:certificatId/pdf` (ARTO-45) : l'URL signée ne dure que
 * cinq minutes, elle se demande donc AU CLIC et ne se garde pas. La colonne
 * « Document » n'a plus de tiret pour ce que la session n'a pas signé.
 */
export function CertificatsTab({
  galerieSlug,
  oeuvre,
  peutEmettre,
  peutAnnuler,
  provenanceActive = false,
}: CertificatsTabProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';

  const certificats = useCertificatsOeuvre(galerieSlug, oeuvre.id);
  const editions = useEditionsOeuvre(galerieSlug, oeuvre.type === 'edition' ? oeuvre.id : null);
  const provenancePublique = useProvenance(galerieSlug, oeuvre.id, provenanceActive);
  const emettre = useEmettreCertificat(galerieSlug);
  const ouvrirPdf = useOuvrirCertificatPdf(galerieSlug);
  const annuler = useAnnulerCertificat(galerieSlug);
  const [acteAAnnuler, setActeAAnnuler] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [motifPublic, setMotifPublic] = useState<MotifPublicAnnulation>('erreur_materielle');
  const [erreurAnnulation, setErreurAnnulation] = useState<string | null>(null);

  async function confirmerAnnulation(): Promise<void> {
    if (!acteAAnnuler) return;
    setErreurAnnulation(null);
    try {
      await annuler.mutateAsync({
        oeuvreId: oeuvre.id,
        certificatId: acteAAnnuler,
        corps: { motif: motif.trim(), motifPublic },
      });
      setActeAAnnuler(null);
    } catch (error) {
      setErreurAnnulation(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  const exemplairesSuivis = editions.data?.type === 'edition' ? editions.data.editions : [];

  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [exemplaireId, setExemplaireId] = useState('');
  const [exemplaireLibre, setExemplaireLibre] = useState('');
  const [provenance, setProvenance] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreurDocument, setErreurDocument] = useState<string | null>(null);
  /** Le certificat dont on demande le document, pour n'animer que sa ligne. */
  const [certificatEnCours, setCertificatEnCours] = useState<string | null>(null);

  async function ouvrirCertificat(certificatId: string): Promise<void> {
    setErreurDocument(null);
    setCertificatEnCours(certificatId);
    try {
      const document = await ouvrirPdf.mutateAsync({ oeuvreId: oeuvre.id, certificatId });
      ouvrirUrlSignee(document.url);
    } catch (error) {
      setErreurDocument(estApiError(error) ? error.message : t('commun:erreurReseau'));
    } finally {
      setCertificatEnCours(null);
    }
  }

  function ouvrirDialogue(): void {
    setExemplaireId('');
    setExemplaireLibre('');
    setProvenance('');
    setErreur(null);
    setDialogueOuvert(true);
  }

  async function confirmerEmission(): Promise<void> {
    setErreur(null);
    const corps: CorpsEmissionCertificat = {
      ...(exemplairesSuivis.length > 0 && exemplaireId ? { exemplaireId } : {}),
      ...(exemplaireLibre.trim() ? { exemplaire: exemplaireLibre.trim() } : {}),
      ...(provenance.trim()
        ? {
            provenance: provenance
              .split('\n')
              .map((ligne) => ligne.trim())
              .filter((ligne) => ligne.length > 0),
          }
        : {}),
    };
    try {
      const reponse = await emettre.mutateAsync({ oeuvreId: oeuvre.id, corps });
      ouvrirUrlSignee(reponse.document.url);
      setDialogueOuvert(false);
    } catch (error) {
      if (estApiError(error)) {
        const signataireAbsent =
          error.code === 'validation.invalide' &&
          error.details !== null &&
          typeof error.details === 'object' &&
          'raison' in error.details &&
          error.details.raison === 'signataire_absent';
        setErreur(
          signataireAbsent ? t('regie:oeuvres.certificats.signataireAbsent') : error.message,
        );
      } else {
        setErreur(t('commun:erreurReseau'));
      }
    }
  }

  return (
    <div className={styles.formulaire}>
      <div>
        <Verrouillable module="documents-prestige" ouvert={peutEmettre}>
          <Button variant="secondary" onClick={ouvrirDialogue}>
            {t('regie:oeuvres.certificats.emettre')}
          </Button>
        </Verrouillable>
      </div>

      {certificats.isLoading ? (
        <p className={styles.vide}>{t('commun:chargement')}</p>
      ) : certificats.isError ? (
        <p className={styles.erreur} role="alert">
          {estApiError(certificats.error) ? certificats.error.message : t('commun:erreurReseau')}
        </p>
      ) : !certificats.data || certificats.data.certificats.length === 0 ? (
        <p className={styles.vide}>{t('regie:oeuvres.certificats.aucun')}</p>
      ) : (
        <table className={styles.tableau}>
          <thead>
            <tr>
              <th scope="col">{t('regie:oeuvres.certificats.colonneNumero')}</th>
              <th scope="col">{t('regie:oeuvres.certificats.colonneDate')}</th>
              <th scope="col">{t('regie:oeuvres.certificats.colonneExemplaire')}</th>
              <th scope="col">{t('regie:oeuvres.certificats.colonneEmisPar')}</th>
              <th scope="col">{t('regie:oeuvres.certificats.colonneDocument')}</th>
              <th scope="col">{t('regie:oeuvres.certificats.etat')}</th>
            </tr>
          </thead>
          <tbody>
            {certificats.data.certificats.map((certificat) => (
              <tr key={certificat.id}>
                <td>{certificat.numero}</td>
                <td>{formatDate(new Date(certificat.emisLe), langue, 'court')}</td>
                <td>{certificat.exemplaire ?? '—'}</td>
                <td>{certificat.signataire.nom}</td>
                <td>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={certificatEnCours === certificat.id}
                    onClick={() => void ouvrirCertificat(certificat.id)}
                  >
                    <Download size={14} strokeWidth={1.5} aria-hidden />
                    {t('regie:oeuvres.certificats.telecharger')}
                  </Button>
                  <a href={certificat.adresseVerification} target="_blank" rel="noreferrer">
                    {t('regie:oeuvres.certificats.verifier')}
                  </a>
                  {peutAnnuler && !certificat.annuleLe ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setMotif('');
                        setMotifPublic('erreur_materielle');
                        setErreurAnnulation(null);
                        setActeAAnnuler(certificat.id);
                      }}
                    >
                      {t('regie:oeuvres.certificats.annulerActe')}
                    </Button>
                  ) : null}
                </td>
                <td>
                  {certificat.annuleLe
                    ? t('regie:oeuvres.certificats.annuleLe', {
                        date: formatDate(new Date(certificat.annuleLe), langue, 'court'),
                      })
                    : t('regie:oeuvres.certificats.valide')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {erreurDocument ? (
        <p className={styles.erreur} role="alert">
          {erreurDocument}
        </p>
      ) : null}

      <Dialog
        open={acteAAnnuler !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert && !annuler.isPending) setActeAAnnuler(null);
        }}
        title={t('regie:oeuvres.certificats.annulerActe')}
        description={t('regie:oeuvres.certificats.annulationConfirmation')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button
              variant="ghost"
              disabled={annuler.isPending}
              onClick={() => setActeAAnnuler(null)}
            >
              {t('commun:actions.annuler')}
            </Button>
            <Button
              loading={annuler.isPending}
              disabled={motif.trim().length < 5 || motif.trim().length > 300}
              onClick={() => void confirmerAnnulation()}
            >
              {t('regie:oeuvres.certificats.annulerActe')}
            </Button>
          </>
        }
      >
        <div className={styles.formulaire}>
          <Field
            label={t('regie:oeuvres.certificats.motifInterne')}
            htmlFor="motif-annulation-certificat"
          >
            <Textarea
              id="motif-annulation-certificat"
              value={motif}
              maxLength={300}
              onChange={(event) => setMotif(event.target.value)}
            />
          </Field>
          <Field
            label={t('regie:oeuvres.certificats.motifPublic')}
            htmlFor="motif-public-certificat"
          >
            <Select
              id="motif-public-certificat"
              value={motifPublic}
              onValueChange={(valeur) => setMotifPublic(valeur as MotifPublicAnnulation)}
              options={MOTIFS_PUBLICS_ANNULATION.map((valeur) => ({
                value: valeur,
                label: t(`regie:oeuvres.certificats.motifs.${valeur}`),
              }))}
            />
          </Field>
          {erreurAnnulation ? (
            <p role="alert" className={styles.erreur}>
              {erreurAnnulation}
            </p>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        open={dialogueOuvert}
        onOpenChange={setDialogueOuvert}
        title={t('regie:oeuvres.certificats.dialogueTitre')}
        description={t('regie:oeuvres.certificats.dialogueTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialogueOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button onClick={() => void confirmerEmission()} loading={emettre.isPending}>
              {t('regie:oeuvres.certificats.confirmer')}
            </Button>
          </>
        }
      >
        <div className={styles.formulaire}>
          {exemplairesSuivis.length > 0 ? (
            <Field
              label={t('regie:oeuvres.certificats.exemplaireChamp')}
              htmlFor="certificat-exemplaire"
            >
              <Select
                id="certificat-exemplaire"
                value={exemplaireId}
                onValueChange={setExemplaireId}
                placeholder={t('regie:oeuvres.certificats.exemplaireTirage')}
                options={[
                  { value: '', label: t('regie:oeuvres.certificats.exemplaireTirage') },
                  ...exemplairesSuivis.map((exemplaire) => ({
                    value: exemplaire.id,
                    label: exemplaire.numero,
                  })),
                ]}
              />
            </Field>
          ) : (
            <Field
              label={t('regie:oeuvres.certificats.exemplaireChamp')}
              htmlFor="certificat-exemplaire-libre"
            >
              <Input
                id="certificat-exemplaire-libre"
                value={exemplaireLibre}
                onChange={(event) => setExemplaireLibre(event.target.value)}
                placeholder={t('regie:oeuvres.certificats.exemplaireTirage')}
              />
            </Field>
          )}
          <Field
            label={t('regie:oeuvres.certificats.provenanceChamp')}
            htmlFor="certificat-provenance"
          >
            <Textarea
              id="certificat-provenance"
              rows={3}
              value={provenance}
              onChange={(event) => setProvenance(event.target.value)}
            />
            <p className={styles.aide}>
              {t(
                provenanceActive
                  ? 'regie:oeuvres.certificats.provenanceAideAuto'
                  : 'regie:oeuvres.certificats.provenanceAide',
              )}
            </p>
            {provenanceActive && (
              <Button
                variant="secondary"
                disabled={!provenancePublique.data}
                onClick={() =>
                  setProvenance(provenancePublique.data?.lignesPubliques.join('\n') ?? '')
                }
              >
                {t('regie:oeuvres.certificats.reprendreProvenance')}
              </Button>
            )}
          </Field>
          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}
